import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const MELLER_ATC_ALLOWED_SOURCE = 'product-form-component';

/**
 * Returns the custom Meller add-to-cart modal.
 * @returns {HTMLDialogElement | null}
 */
const getMellerAtcModal = () => {
  const modal = document.getElementById('meller-atc-modal');
  return modal instanceof HTMLDialogElement ? modal : null;
};

/**
 * Returns true when the lateral cart drawer is currently open.
 * @returns {boolean}
 */
const isCartDrawerOpen = () => {
  const drawerComponent = document.querySelector('cart-drawer-component');
  const drawerDialog = drawerComponent?.querySelector('dialog');

  return drawerDialog instanceof HTMLDialogElement && drawerDialog.open;
};

/**
 * Prevents the central modal from being stacked over the lateral cart drawer.
 */
const closeMellerAtcModal = () => {
  const modal = getMellerAtcModal();

  if (!modal?.open) return;

  try {
    modal.close();
  } catch (error) {
    // No-op. Native dialog can throw if it is already closing.
  }
};

/**
 * Only real product-form additions should open the central add-to-cart modal.
 *
 * Important:
 * CartAddEvent and CartUpdateEvent both use the same internal event name: cart:update.
 * Quantity changes and removals inside the cart drawer also emit cart:update,
 * so we must ignore those updates here.
 *
 * @param {Object} [detail]
 * @returns {boolean}
 */
const shouldOpenMellerAtcModal = (detail = {}) => {
  if (!detail || detail.didError) return false;

  /**
   * Allowed:
   * - PDP add to cart
   * - Quick add modal add to cart
   *
   * Blocked:
   * - Cart drawer quantity changes
   * - Cart drawer item removals
   * - Discount/cart recalculations
   * - Any cart:update not generated by product-form-component
   */
  if (detail.source !== MELLER_ATC_ALLOWED_SOURCE) return false;

  /**
   * If the lateral drawer is already open, never open the central modal.
   * This fixes the mobile issue where removing items from the drawer opens
   * the central cart modal unnecessarily.
   */
  if (isCartDrawerOpen()) return false;

  return true;
};

/**
 * Opens the custom Meller add-to-cart modal immediately with a lightweight state.
 * The existing modal script keeps responsibility for fetching/rendering the full cart.
 *
 * @param {Object} [detail]
 */
const openMellerAtcModalImmediately = (detail = {}) => {
  if (!shouldOpenMellerAtcModal(detail)) return;

  const modal = getMellerAtcModal();

  if (!modal) return;

  const itemsEl = document.getElementById('meller-atc-items');
  const cartCountEl = document.getElementById('meller-atc-cart-count');
  const cartTotalEl = document.getElementById('meller-atc-cart-total');

  if (cartCountEl && !cartCountEl.textContent.trim()) {
    cartCountEl.textContent = 'Actualizando carrito';
  }

  if (cartTotalEl && !cartTotalEl.textContent.trim()) {
    cartTotalEl.textContent = 'Actualizando total…';
  }

  if (itemsEl && !itemsEl.children.length) {
    itemsEl.innerHTML = `
      <div class="meller-atc-modal__empty">
        Actualizando carrito…
      </div>
    `;
  }

  if (modal.open) return;

  requestAnimationFrame(() => {
    try {
      if (!modal.open && !isCartDrawerOpen()) modal.showModal();
    } catch (error) {
      // The normal cart drawer flow remains as fallback.
    }
  });
};

document.addEventListener(
  'meller:cart-added',
  (event) => {
    openMellerAtcModalImmediately(event.detail || {});
  },
  true
);

/**
 * Cart drawer:
 * - Click en icono carrito => drawer lateral normal
 * - Add to cart real => feedback inmediato + modal Meller
 * - Remover/sumar/restar desde drawer => nunca abre modal central
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    const detail = event?.detail?.data || {};

    if (!shouldOpenMellerAtcModal(detail)) return;

    openMellerAtcModalImmediately(detail);

    document.dispatchEvent(
      new CustomEvent('meller:cart-added', {
        bubbles: true,
        detail,
      })
    );
  };

  open() {
    /**
     * If the user opens the lateral cart drawer, the central modal must close.
     * This avoids stacked cart experiences on mobile.
     */
    closeMellerAtcModal();

    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}